import * as React from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  ExternalLink,
  MoreHorizontal,
  Plus,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { PLAN_FEATURES } from "@degaus/shared/plans";
import { formatCurrencyInCents } from "@degaus/shared/utils";

import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { StatusPill } from "@/components/ui/StatusPill";
import { useUser } from "@/hooks/useUser";
import { CliFooter } from "@/landing/components/CliFooter";
import { CliNav } from "@/landing/components/CliNav";
import { Route as rootRoute } from "@/routes/__root";

// Plans an admin can assign to a new seat. Mirrors the public pricing
// page, which sells one plan (Pro = WONDA). Existing seats on other plan
// codes (WONDA_PREMIUM, PREMIUM, CREATOR, etc.) keep displaying their label
// via PLAN_FEATURES even though they aren't selectable on a fresh seat.
const ASSIGNABLE_SEAT_PLANS = ["WONDA"] as const;
const NO_SEAT_VALUE = "NONE";

const USAGE_REPORT_PRESETS = {
  "this-month": "This month",
  "last-month": "Last month",
  "last-3-months": "Last 3 months",
  custom: "Custom range",
} as const;

type UsageReportPreset = keyof typeof USAGE_REPORT_PRESETS;

// Stripe returns lowercase brand strings like "visa", "amex", "mastercard". Display them in their conventional cased form.
function formatPaymentMethodBrand(brand: string | null): string {
  if (!brand) return "Card";
  const labels: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return (
    labels[brand.toLowerCase()] ??
    brand.charAt(0).toUpperCase() + brand.slice(1)
  );
}

function getSeatPlanLabel(planCode: string | null): string {
  if (!planCode) return "Basic";
  return (
    PLAN_FEATURES[planCode as keyof typeof PLAN_FEATURES]?.label ?? planCode
  );
}

export const Route = createFileRoute(
  "/_authenticated/(wonda)/_wonda/organizations/$slug",
)({
  component: OrgDetailPage,
});

function OrgDetailPage() {
  const { slug } = useParams({ strict: false });
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });

  const { data: orgs } = api.organization.list.useQuery(undefined);
  const matched = orgs?.find((row) => row.slug === slug);
  const organizationId = matched?.organizationId;

  if (!orgs) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-12">
          <Spinner variant="wonda" />
        </div>
      </Shell>
    );
  }

  if (!organizationId) {
    return (
      <Shell>
        <p className="text-sm text-(--cli-text-muted)">
          Organization not found. <Link to="/organizations">Back to list</Link>
        </p>
      </Shell>
    );
  }

  // Detail page is the management surface — admins/owners only. Regular members get a read-only view from /account/organizations and shouldn't land on this page even by direct URL.
  const isAdminOrOwner =
    matched?.isOwner === true || matched?.clerkRole === "admin";
  if (!isAdminOrOwner) {
    return (
      <Shell>
        <p className="text-sm text-(--cli-text-muted)">
          You don't have access to manage this organization. Ask an admin if you
          need changes.{" "}
          <Link to="/account/organizations">Back to your organizations</Link>.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <OrgDetailContent organizationId={organizationId} />
    </Shell>
  );
}

// Shell shared by every org-detail branch. The light
// ground comes from the global html/body base paint.
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] flex-col">
      <CliNav />
      <main className="mx-auto min-h-[70vh] w-full max-w-4xl px-4 py-8 sm:px-6">
        <Link
          to="/organizations"
          className="mb-6 inline-flex items-center gap-1 text-sm text-(--cli-text-muted) transition-colors hover:text-(--cli-text)"
        >
          <ArrowLeft className="size-4" strokeWidth={1.5} />
          All organizations
        </Link>
        {children}
      </main>
      <CliFooter />
    </div>
  );
}

function OrgDetailContent({ organizationId }: { organizationId: string }) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const apiUtils = api.useUtils();
  const [gatePromptOpen, setGatePromptOpen] = React.useState(false);
  const [topupOpen, setTopupOpen] = React.useState(false);
  // Fresh nonce per dialog open. Reused across retry clicks of the same
  // dialog so duplicate submits collapse to one Stripe charge via the
  // Idempotency-Key. New nonce on next open.
  const [topupNonce, setTopupNonce] = React.useState(() =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`,
  );
  React.useEffect(() => {
    if (topupOpen && typeof crypto !== "undefined" && "randomUUID" in crypto) {
      setTopupNonce(crypto.randomUUID());
    }
  }, [topupOpen]);

  const { data, isLoading } = api.organization.get.useQuery({
    organizationId,
  });
  const balanceQuery = api.organization.balance.useQuery({ organizationId });

  // Seat changes settle in two phases: setMemberPlan returns once Stripe
  // accepts the subscription change, but the per-seat credit grant lands
  // a few seconds later via the invoice.paid webhook. Re-invalidate over
  // a short window so the wallet refreshes without a manual reload.
  const invalidateAfterSeatChange = React.useCallback(() => {
    const refresh = () => {
      void apiUtils.organization.get.invalidate({ organizationId });
      void apiUtils.organization.balance.invalidate({ organizationId });
      void apiUtils.organization.transactions.invalidate({ organizationId });
    };
    for (const ms of [0, 1500, 3500, 6000, 10000]) {
      setTimeout(refresh, ms);
    }
  }, [apiUtils, organizationId]);

  const openPortal = api.organization.billing.openPortal.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => toast.error(error.message),
  });
  const startAddPaymentMethod =
    api.organization.billing.startAddPaymentMethod.useMutation({
      onSuccess: ({ url }) => {
        window.location.href = url;
      },
      onError: (error) => toast.error(error.message),
    });
  const startTopup = api.organization.billing.startTopup.useMutation({
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => toast.error(error.message),
  });
  const chargeTopup = api.organization.billing.chargeTopup.useMutation({
    onSuccess: (result) => {
      // Off-session can fail with 3DS / SCA on EU cards. In that case
      // fall through to Stripe Checkout so the admin can complete the
      // auth challenge in Stripe's hosted UI for that one charge.
      if (result.kind === "authentication_required") {
        toast.message(
          "Your card needs an extra verification step. Continuing to Stripe...",
        );
        return;
      }
      toast.success("Top-up complete");
      setTopupOpen(false);
      // chargeOrgTopup returns once the Stripe PaymentIntent succeeds, but the credit_transaction row is written by the charge.succeeded webhook a beat later. Poll the balance + transactions over a short window so the new credit shows up without a manual refresh.
      const refetch = () => {
        void apiUtils.organization.balance.invalidate({ organizationId });
        void apiUtils.organization.transactions.invalidate({ organizationId });
      };
      for (const ms of [0, 1500, 3500, 6000]) {
        setTimeout(refetch, ms);
      }
    },
    onError: (error) => toast.error(error.message),
  });

  // After Stripe Checkout setup-mode redirects back, the gate flag flips
  // via webhook a moment later. Refetch on mount when the URL signals a
  // successful setup so the user sees the unlocked UI quickly.
  const hasSetupSuccess =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("setup") === "success";
  React.useEffect(() => {
    if (!hasSetupSuccess) return;
    void apiUtils.organization.get.invalidate({ organizationId });
    const timer = setTimeout(() => {
      void apiUtils.organization.get.invalidate({ organizationId });
    }, 1500);
    return () => clearTimeout(timer);
  }, [hasSetupSuccess, organizationId, apiUtils]);

  // After Stripe Checkout payment-mode for top-up redirects back, the
  // credit_transaction lands via webhook a moment later. Refetch the
  // balance + transactions so the new balance shows up promptly.
  const hasTopupSuccess =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("topup") === "success";
  React.useEffect(() => {
    if (!hasTopupSuccess) return;
    const refetch = () => {
      void apiUtils.organization.balance.invalidate({ organizationId });
      void apiUtils.organization.transactions.invalidate({ organizationId });
    };
    refetch();
    const t1 = setTimeout(refetch, 1500);
    const t2 = setTimeout(refetch, 4000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hasTopupSuccess, organizationId, apiUtils]);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner variant="wonda" />
      </div>
    );
  }

  const balanceUsd = balanceQuery.data
    ? formatMicrodollars(balanceQuery.data.balanceMicrodollars)
    : "...";

  const hasPaymentMethod = data.organization.hasPaymentMethod;
  const startSetup = () =>
    startAddPaymentMethod.mutate({
      organizationId,
      successUrl: `${window.location.origin}${window.location.pathname}?setup=success`,
      cancelUrl: window.location.href,
    });
  const promptSetup = () => setGatePromptOpen(true);

  return (
    <div className="flex flex-col gap-8">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">{data.organization.name}</h1>
          <p className="mt-1 text-sm text-(--cli-text-muted)">
            {data.organization.slug}
          </p>
        </div>
        <OrgActionsMenu
          organizationId={organizationId}
          slug={data.organization.slug}
          name={data.organization.name}
          members={data.members}
        />
      </header>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard label="Wallet balance" value={balanceUsd} />
        <StatCard label="Members" value={String(data.members.length)} />
      </section>

      <CliUsageCard slug={data.organization.slug} />

      {!hasPaymentMethod ? (
        <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) px-(--cli-card-gutter) py-8">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-(--hair) p-2">
              <CreditCard className="size-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-medium">
                Add a payment method to continue
              </h2>
              <p className="mt-1 text-sm text-(--cli-text-muted)">
                You need a card on file before inviting members or assigning
                plans. Billing is per organization, separate from your personal
                account.
              </p>
              <Button
                variant="steward"
                className="mt-4"
                onClick={startSetup}
                disabled={startAddPaymentMethod.isPending}
              >
                {startAddPaymentMethod.isPending
                  ? "Opening..."
                  : "Set up payment method"}
                <ExternalLink className="size-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) px-(--cli-card-gutter) py-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-medium">Members</h2>
          <InviteMemberButton
            organizationId={organizationId}
            disabled={!hasPaymentMethod}
            onDisabledClick={promptSetup}
            onInvited={() =>
              void apiUtils.organization.get.invalidate({ organizationId })
            }
          />
        </div>
        <MembersTable
          organizationId={organizationId}
          members={data.members}
          gated={!hasPaymentMethod}
          onGatedClick={promptSetup}
          onChanged={() => invalidateAfterSeatChange()}
        />
        {data.pendingInvitations.length > 0 ? (
          <PendingInvitationsList
            organizationId={organizationId}
            invitations={data.pendingInvitations}
            onChanged={() =>
              void apiUtils.organization.get.invalidate({ organizationId })
            }
          />
        ) : null}
      </section>

      <PaymentMethodGatePrompt
        open={gatePromptOpen}
        onOpenChange={setGatePromptOpen}
        onConfirm={() => {
          setGatePromptOpen(false);
          startSetup();
        }}
        pending={startAddPaymentMethod.isPending}
      />

      <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) px-(--cli-card-gutter) py-8">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-medium">Billing</h2>
          {hasPaymentMethod ? (
            <StatusPill tone="success" className="gap-1.5">
              <CheckCircle2 className="size-3.5" strokeWidth={2} />
              {data.organization.paymentMethodLast4
                ? `${formatPaymentMethodBrand(data.organization.paymentMethodBrand)} ending in ${data.organization.paymentMethodLast4}`
                : "Payment method added"}
            </StatusPill>
          ) : null}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            variant="steward"
            onClick={() => {
              if (!hasPaymentMethod) {
                promptSetup();
                return;
              }
              setTopupOpen(true);
            }}
            disabled={startTopup.isPending}
          >
            {startTopup.isPending ? "Opening..." : "Top up wallet"}
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              openPortal.mutate({
                organizationId,
                returnUrl: window.location.href,
              })
            }
            disabled={openPortal.isPending}
          >
            {openPortal.isPending ? "Opening..." : "Open billing portal"}
            <ExternalLink className="size-4" strokeWidth={1.5} />
          </Button>
        </div>
        <AutoTopupStatus organizationId={organizationId} />
      </section>

      <InvoicesTable organizationId={organizationId} />

      <UsageTable organizationId={organizationId} members={data.members} />

      <ProjectsCard organizationId={organizationId} />

      <TopupDialog
        open={topupOpen}
        onOpenChange={setTopupOpen}
        hasPaymentMethod={hasPaymentMethod}
        onConfirm={async ({ creditsInDollars, autoTopup }) => {
          // When the org has a saved card AND no auto-topup change is
          // requested, charge the saved card off-session for an
          // immediate in-place top-up. The auto-topup-config write is
          // tied to the Stripe Checkout flow (it captures the card), so
          // any auto-topup setup goes through startTopup instead.
          if (hasPaymentMethod && !autoTopup) {
            const result = await chargeTopup.mutateAsync({
              organizationId,
              creditsInDollars,
              clientNonce: topupNonce,
            });
            if (result.kind === "succeeded") return;
            // 3DS / SCA: hand off to Checkout for this one charge.
          }
          startTopup.mutate({
            organizationId,
            creditsInDollars,
            successUrl: `${window.location.origin}${window.location.pathname}?topup=success`,
            cancelUrl: window.location.href,
            ...(autoTopup
              ? {
                  enableAutoTopup: true,
                  autoTopupAmountMicrodollars: Math.round(
                    autoTopup.amount * 1_000_000,
                  ),
                  autoTopupThresholdMicrodollars: Math.round(
                    autoTopup.threshold * 1_000_000,
                  ),
                }
              : {}),
          });
        }}
        pending={startTopup.isPending || chargeTopup.isPending}
      />
    </div>
  );
}

function UsageTable({
  organizationId,
  members,
}: {
  organizationId: string;
  members: MemberRow[];
}) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const apiUtils = api.useUtils();
  // Stack of cursors: [undefined, c1, c2, ...]; the last entry is the cursor
  // used for the currently-displayed page. Push next-page cursor on Next,
  // pop on Prev. Tracks history without needing a server-side count.
  const [cursorStack, setCursorStack] = React.useState<(string | undefined)[]>([
    undefined,
  ]);
  const [includeHolds, setIncludeHolds] = React.useState(false);
  const [reportPreset, setReportPreset] =
    React.useState<UsageReportPreset>("this-month");
  const [reportCustomFrom, setReportCustomFrom] = React.useState("");
  const [reportCustomTo, setReportCustomTo] = React.useState("");
  const [isDownloadingReport, setIsDownloadingReport] = React.useState(false);
  const currentCursor = cursorStack[cursorStack.length - 1];

  async function handleDownloadReport() {
    const { from, to } =
      reportPreset === "custom"
        ? { from: reportCustomFrom, to: reportCustomTo }
        : getUsageReportPeriod(reportPreset);
    if (!from || !to) {
      toast.error("Pick a start and end date for the custom range");
      return;
    }
    setIsDownloadingReport(true);
    try {
      const { fileName, base64 } =
        await apiUtils.organization.usageReport.fetch({
          organizationId,
          from,
          to,
        });
      const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to download report",
      );
    } finally {
      setIsDownloadingReport(false);
    }
  }

  const { data, isLoading } = api.organization.transactions.useQuery({
    organizationId,
    limit: 10,
    cursor: currentCursor,
    includeHolds,
  });

  const memberByAccountId = React.useMemo(() => {
    const map = new Map<string, MemberRow>();
    for (const member of members) map.set(member.accountId, member);
    return map;
  }, [members]);

  return (
    <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) px-(--cli-card-gutter) py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-medium">Usage</h2>
        <button
          type="button"
          onClick={() => {
            setIncludeHolds((prev) => !prev);
            setCursorStack([undefined]);
          }}
          className={`rounded-full px-2.5 py-0.5 text-xs transition-colors ${
            includeHolds
              ? "bg-(--cli-accent)/15 text-(--cli-accent)"
              : "bg-(--hair) text-(--cli-text-muted) hover:bg-(--hair)"
          }`}
        >
          {includeHolds ? "Hide holds & releases" : "Show holds & releases"}
        </button>
      </div>
      <p className="mt-1 text-sm text-(--cli-text-muted)">
        Recent credit activity across the organization wallet, newest first.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select
          value={reportPreset}
          onValueChange={(value) => setReportPreset(value as UsageReportPreset)}
        >
          <SelectTrigger className="h-8 w-[150px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(USAGE_REPORT_PRESETS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {reportPreset === "custom" ? (
          <>
            <Input
              type="date"
              value={reportCustomFrom}
              onChange={(event) => setReportCustomFrom(event.target.value)}
              className="h-8 w-[140px] text-xs"
              aria-label="Report start date"
            />
            <span className="text-xs text-(--cli-text-muted)">to</span>
            <Input
              type="date"
              value={reportCustomTo}
              onChange={(event) => setReportCustomTo(event.target.value)}
              className="h-8 w-[140px] text-xs"
              aria-label="Report end date"
            />
          </>
        ) : null}
        <Button
          variant="ghost"
          size="xs"
          className="w-8 px-0"
          onClick={handleDownloadReport}
          disabled={isDownloadingReport}
          aria-label="Download Excel report"
          title="Download Excel report"
        >
          {isDownloadingReport ? (
            <Spinner variant="wonda" size="sm" />
          ) : (
            <Download className="size-4" strokeWidth={1.5} />
          )}
        </Button>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-(--cli-text-muted)">
            <tr className="border-b border-(--hair)">
              <th className="px-2 py-2 text-left font-normal">When</th>
              <th className="px-2 py-2 text-left font-normal">Member</th>
              <th className="px-2 py-2 text-left font-normal">Type</th>
              <th className="px-2 py-2 text-left font-normal">Description</th>
              <th className="px-2 py-2 text-right font-normal">Amount</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-2 py-6 text-center">
                  <Spinner variant="wonda" size="sm" />
                </td>
              </tr>
            ) : !data || data.items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-6 text-center text-(--cli-text-muted)"
                >
                  No transactions yet.
                </td>
              </tr>
            ) : (
              data.items.map((row) => {
                const member = row.accountId
                  ? memberByAccountId.get(row.accountId)
                  : undefined;
                const memberLabel = member
                  ? member.email
                  : row.accountId
                    ? "Former member"
                    : "—";
                return (
                  <tr
                    key={row.creditTransactionId}
                    className="border-b border-(--hair)"
                  >
                    <td className="px-2 py-2 whitespace-nowrap">
                      {formatDate(row.createdAt)}
                    </td>
                    <td className="px-2 py-2">{memberLabel}</td>
                    <td className="px-2 py-2 capitalize">{row.type}</td>
                    <td className="px-2 py-2 text-(--cli-text-muted)">
                      {row.message ?? row.sourceKind ?? ""}
                    </td>
                    <td
                      className={`px-2 py-2 text-right font-mono ${
                        BigInt(row.amountMicrodollars ?? "0") < 0n
                          ? "text-(--cli-syntax-red)"
                          : "text-(--cli-syntax-green)"
                      }`}
                    >
                      {formatMicrodollars(row.amountMicrodollars ?? "0")}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCursorStack((stack) => stack.slice(0, -1))}
          disabled={cursorStack.length <= 1}
        >
          <ChevronLeft className="size-4" strokeWidth={1.5} />
          Previous
        </Button>
        <span className="text-xs text-(--cli-text-muted)">
          Page {cursorStack.length}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            setCursorStack((stack) => [...stack, data?.nextCursor ?? undefined])
          }
          disabled={!data?.nextCursor}
        >
          Next
          <ChevronRight className="size-4" strokeWidth={1.5} />
        </Button>
      </div>
    </section>
  );
}

function formatDate(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** UTC date range (YYYY-MM-DD, end inclusive) for a usage report preset. */
function getUsageReportPeriod(preset: Exclude<UsageReportPreset, "custom">): {
  from: string;
  to: string;
} {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);
  const today = toIsoDate(now);
  switch (preset) {
    case "this-month":
      return { from: toIsoDate(new Date(Date.UTC(year, month, 1))), to: today };
    case "last-month":
      return {
        from: toIsoDate(new Date(Date.UTC(year, month - 1, 1))),
        // Day 0 of the current month = last day of the previous month.
        to: toIsoDate(new Date(Date.UTC(year, month, 0))),
      };
    case "last-3-months":
      return {
        from: toIsoDate(new Date(Date.UTC(year, month - 2, 1))),
        to: today,
      };
  }
}

function formatInvoiceStatus(status: string | null): {
  label: string;
  tone: "paid" | "open" | "void" | "neutral";
} {
  switch (status) {
    case "paid":
      return { label: "Paid", tone: "paid" };
    case "open":
      return { label: "Awaiting payment", tone: "open" };
    case "uncollectible":
      return { label: "Uncollectible", tone: "open" };
    case "void":
      return { label: "Void", tone: "void" };
    case "draft":
      return { label: "Draft", tone: "neutral" };
    default:
      return { label: status ?? "—", tone: "neutral" };
  }
}

function ProjectsCard({ organizationId }: { organizationId: string }) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const apiUtils = api.useUtils();
  const [isAddingProject, setIsAddingProject] = React.useState(false);
  const [newProjectName, setNewProjectName] = React.useState("");

  const { data: projects, isLoading } = api.organization.projects.list.useQuery(
    { organizationId },
  );

  const createProject = api.organization.projects.create.useMutation({
    onSuccess: async () => {
      setNewProjectName("");
      setIsAddingProject(false);
      await apiUtils.organization.projects.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteProject = api.organization.projects.delete.useMutation({
    onSuccess: async () => {
      await apiUtils.organization.projects.list.invalidate();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) px-(--cli-card-gutter) py-8">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium">Projects</h2>
        <Button
          variant="ghost"
          size="xs"
          className="w-8 px-0"
          onClick={() => setIsAddingProject(true)}
          aria-label="New project"
          title="New project"
        >
          <Plus className="size-4" strokeWidth={1.5} />
        </Button>
      </div>
      <p className="mt-1 text-sm text-(--cli-text-muted)">
        Spend-tagging projects members can attribute usage to (wonda use
        --project &lt;name&gt;). Usage reports and the Excel export group spend
        by project.
      </p>
      <Dialog
        open={isAddingProject}
        onOpenChange={(open) => {
          setIsAddingProject(open);
          if (!open) setNewProjectName("");
        }}
      >
        <DialogContent>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (newProjectName.trim().length === 0) return;
              createProject.mutate({ organizationId, name: newProjectName });
            }}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>New project</DialogTitle>
              <DialogDescription>
                Members tag their spend against it with wonda use --project
                &lt;name&gt;. Names are unique per organization (case doesn't
                matter).
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="new-project-name">Name</Label>
              <Input
                id="new-project-name"
                value={newProjectName}
                onChange={(event) => setNewProjectName(event.target.value)}
                placeholder="acme-launch"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={
                  createProject.isPending || newProjectName.trim().length === 0
                }
              >
                {createProject.isPending ? (
                  <Spinner variant="wonda" size="sm" />
                ) : (
                  "Create project"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <div className="mt-4">
        {isLoading ? (
          <Spinner variant="wonda" size="sm" />
        ) : !projects || projects.length === 0 ? (
          <p className="text-sm text-(--cli-text-muted)">No projects yet.</p>
        ) : (
          <ul className="divide-y divide-white/[.04]">
            {projects.map((project) => (
              <li
                key={project.projectId}
                className="flex items-center justify-between py-2 text-sm"
              >
                <span>{project.name}</span>
                <Button
                  variant="ghost"
                  size="xs"
                  className="w-8 px-0"
                  onClick={() =>
                    deleteProject.mutate({ organizationId, name: project.name })
                  }
                  disabled={deleteProject.isPending}
                  aria-label={`Delete project ${project.name}`}
                  title="Delete project (history keeps the name)"
                >
                  <Trash2 className="size-4" strokeWidth={1.5} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function InvoicesTable({ organizationId }: { organizationId: string }) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const { data, isLoading } = api.organization.billing.listInvoices.useQuery({
    organizationId,
  });

  return (
    <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) px-(--cli-card-gutter) py-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-medium">Invoices</h2>
      </div>
      <p className="mt-1 text-sm text-(--cli-text-muted)">
        Recent Stripe invoices for this organization. Open invoices may require
        you to authenticate the payment.
      </p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-(--cli-text-muted)">
            <tr className="border-b border-(--hair)">
              <th className="px-2 py-2 text-left font-normal">Date</th>
              <th className="px-2 py-2 text-left font-normal">Number</th>
              <th className="px-2 py-2 text-left font-normal">Status</th>
              <th className="px-2 py-2 text-right font-normal">Amount</th>
              <th className="px-2 py-2 text-right font-normal">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-2 py-6 text-center">
                  <Spinner variant="wonda" size="sm" />
                </td>
              </tr>
            ) : !data || data.items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-6 text-center text-(--cli-text-muted)"
                >
                  No invoices yet.
                </td>
              </tr>
            ) : (
              data.items.map((invoice) => {
                const status = formatInvoiceStatus(invoice.status);
                const isOpen =
                  invoice.status === "open" ||
                  invoice.status === "uncollectible";
                const isPaid = invoice.status === "paid";
                const pillTone =
                  status.tone === "paid"
                    ? "success"
                    : status.tone === "open"
                      ? "warning"
                      : "neutral";
                return (
                  <tr key={invoice.id} className="border-b border-(--hair)">
                    <td className="px-2 py-2 whitespace-nowrap">
                      {formatDate(new Date(invoice.created * 1000))}
                    </td>
                    <td className="px-2 py-2 font-mono text-xs text-(--cli-text-muted)">
                      {invoice.number ?? "—"}
                    </td>
                    <td className="px-2 py-2">
                      <StatusPill tone={pillTone}>{status.label}</StatusPill>
                    </td>
                    <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
                      {formatCurrencyInCents(invoice.total, invoice.currency)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {isOpen && invoice.hostedInvoiceUrl ? (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-(--cli-accent) hover:underline"
                        >
                          Pay
                          <ExternalLink
                            className="size-3.5"
                            strokeWidth={1.5}
                          />
                        </a>
                      ) : isPaid && invoice.invoicePdf ? (
                        <a
                          href={invoice.invoicePdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-(--cli-text-muted) hover:text-(--cli-text)"
                        >
                          PDF
                          <Download className="size-3.5" strokeWidth={1.5} />
                        </a>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AutoTopupStatus({ organizationId }: { organizationId: string }) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const apiUtils = api.useUtils();
  const { data } = api.organization.billing.getAutoTopup.useQuery({
    organizationId,
  });
  const disable = api.organization.billing.disableAutoTopup.useMutation({
    onSuccess: () => {
      toast.success("Auto top-up disabled");
      void apiUtils.organization.billing.getAutoTopup.invalidate({
        organizationId,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  if (!data) {
    return (
      <p className="mt-3 text-xs text-(--cli-text-muted)">
        Auto top-up is off. Enable it when topping up to keep the wallet funded
        automatically.
      </p>
    );
  }

  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-(--hair) bg-(--ground) p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm">
          <span className="font-medium">Auto top-up:</span>{" "}
          {formatMicrodollars(data.amountMicrodollars)} when balance falls below{" "}
          {formatMicrodollars(data.thresholdMicrodollars)}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (!window.confirm("Disable auto top-up for this organization?"))
              return;
            disable.mutate({ organizationId });
          }}
          disabled={disable.isPending}
        >
          {disable.isPending ? "Disabling..." : "Disable"}
        </Button>
      </div>
    </div>
  );
}

function TopupDialog({
  open,
  onOpenChange,
  hasPaymentMethod,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  hasPaymentMethod: boolean;
  onConfirm: (args: {
    creditsInDollars: number;
    autoTopup: { amount: number; threshold: number } | null;
  }) => void;
  pending: boolean;
}) {
  const [dollars, setDollars] = React.useState("50");
  const [autoTopupOn, setAutoTopupOn] = React.useState(false);
  const [autoAmount, setAutoAmount] = React.useState("50");
  const [autoThreshold, setAutoThreshold] = React.useState("10");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const parsed = Number(dollars.trim());
            if (!Number.isFinite(parsed) || parsed < 5 || parsed > 5000) {
              toast.error("Enter an amount between $5 and $5000");
              return;
            }
            let autoTopup: { amount: number; threshold: number } | null = null;
            if (autoTopupOn) {
              const amount = Number(autoAmount.trim());
              const threshold = Number(autoThreshold.trim());
              if (!Number.isFinite(amount) || amount < 5) {
                toast.error("Auto top-up amount must be at least $5");
                return;
              }
              if (!Number.isFinite(threshold) || threshold <= 0) {
                toast.error("Threshold must be a positive number");
                return;
              }
              if (threshold >= amount) {
                toast.error("Threshold must be less than the top-up amount");
                return;
              }
              autoTopup = { amount, threshold };
            }
            onConfirm({ creditsInDollars: parsed, autoTopup });
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Top up org wallet</DialogTitle>
            <DialogDescription>
              Add credits to the organization wallet. Members with active seats
              spend from this balance.
              {hasPaymentMethod
                ? " The amount will be charged to the org's saved card on file."
                : " You'll be redirected to Stripe to complete payment."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="topup-amount">Amount (USD)</Label>
            <Input
              id="topup-amount"
              value={dollars}
              onChange={(event) => setDollars(event.target.value)}
              placeholder="50"
              inputMode="decimal"
              autoFocus
            />
            <p className="text-xs text-(--cli-text-muted)">
              Minimum $5, maximum $5000. $1 = $1 of credits.
            </p>
          </div>
          <div className="overflow-hidden rounded-lg border border-(--hair) bg-(--ground) p-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={autoTopupOn}
                onChange={(event) => setAutoTopupOn(event.target.checked)}
                className="size-4 rounded border-(--hair-strong) bg-transparent"
              />
              Enable auto top-up
            </label>
            {autoTopupOn ? (
              <div className="mt-3 flex flex-col gap-3">
                <p className="text-xs text-(--cli-text-muted)">
                  When the wallet drops below the threshold, automatically
                  charge the saved card and add credits.
                </p>
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="auto-amount"
                    className="text-xs text-(--cli-text-muted)"
                  >
                    Top-up amount (USD)
                  </Label>
                  <Input
                    id="auto-amount"
                    value={autoAmount}
                    onChange={(event) => setAutoAmount(event.target.value)}
                    placeholder="50"
                    inputMode="decimal"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="auto-threshold"
                    className="text-xs text-(--cli-text-muted)"
                  >
                    Threshold (USD)
                  </Label>
                  <Input
                    id="auto-threshold"
                    value={autoThreshold}
                    onChange={(event) => setAutoThreshold(event.target.value)}
                    placeholder="10"
                    inputMode="decimal"
                  />
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="steward" disabled={pending}>
              {pending
                ? "Charging..."
                : hasPaymentMethod && !autoTopupOn
                  ? `Charge $${dollars || "0"} to saved card`
                  : "Continue to payment"}
              {hasPaymentMethod && !autoTopupOn ? null : (
                <ExternalLink className="size-4" strokeWidth={1.5} />
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentMethodGatePrompt({
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set up a payment method first</DialogTitle>
          <DialogDescription>
            Add a card to the organization before inviting members or assigning
            plans. Billing is per organization, separate from your personal
            account.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="steward"
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? "Opening..." : "Set up payment method"}
            <ExternalLink className="size-4" strokeWidth={1.5} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) p-4">
      <p className="text-xs text-(--cli-text-muted)">{label}</p>
      <p className="mt-1 text-lg font-medium">{value}</p>
    </div>
  );
}

function CliUsageCard({ slug }: { slug: string }) {
  const [copied, setCopied] = React.useState(false);
  const command = `wonda use --org ${slug}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <section className="overflow-hidden rounded-xl border border-(--hair) bg-(--ground) p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-medium">Use from the CLI</h2>
        <a
          href="https://wonda.sh/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-(--cli-text-muted) underline-offset-4 hover:underline"
        >
          Docs <ExternalLink className="inline size-3" strokeWidth={1.5} />
        </a>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-(--hair) px-3 py-2 font-mono text-sm">
          {command}
        </code>
        <Button variant="ghost" size="sm" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p className="mt-3 text-xs text-(--cli-text-muted)">
        Spend goes to this organization's wallet. Paid features (skills, etc.)
        flow through your assigned seat plan.
      </p>
    </section>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InviteMemberButton({
  organizationId,
  disabled,
  onDisabledClick,
  onInvited,
}: {
  organizationId: string;
  disabled: boolean;
  onDisabledClick: () => void;
  onInvited: () => void;
}) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const [open, setOpen] = React.useState(false);
  const [emails, setEmails] = React.useState<string[]>([]);
  const [draft, setDraft] = React.useState("");
  const [role, setRole] = React.useState<"org:member" | "org:admin">(
    "org:member",
  );

  const inviteBulk = api.organization.member.inviteBulk.useMutation({
    onSuccess: ({ ok, failed }) => {
      if (ok.length > 0 && failed.length === 0) {
        toast.success(
          ok.length === 1 ? "Invitation sent" : `${ok.length} invitations sent`,
        );
        resetAndClose();
        onInvited();
      } else if (ok.length > 0 && failed.length > 0) {
        toast.warning(
          `${ok.length} sent, ${failed.length} failed: ${failed
            .map((fail) => fail.email)
            .join(", ")}`,
        );
        // Keep failed addresses in the field so the user can fix or
        // remove them; drop the successful ones.
        setEmails(failed.map((fail) => fail.email));
        onInvited();
      } else {
        toast.error(
          `All invites failed: ${failed.map((fail) => `${fail.email} (${fail.error})`).join("; ")}`,
        );
      }
    },
    onError: (error) => toast.error(error.message),
  });

  const resetAndClose = () => {
    setEmails([]);
    setDraft("");
    setOpen(false);
  };

  // Commit the draft text as one or more email pills. Splits on commas,
  // semicolons, and whitespace so a paste like "a@b.com, c@d.com x@y.com"
  // becomes three chips at once.
  const commitDraft = (text: string) => {
    const parts = text
      .split(/[,;\s]+/)
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean);
    if (parts.length === 0) return;
    setEmails((prev) => {
      const seen = new Set(prev);
      for (const part of parts) {
        if (!seen.has(part)) {
          seen.add(part);
          prev = [...prev, part];
        }
      }
      return prev;
    });
    setDraft("");
  };

  const removeEmail = (email: string) =>
    setEmails((prev) => prev.filter((value) => value !== email));

  if (disabled) {
    return (
      <Button variant="ghost" size="sm" onClick={onDisabledClick}>
        <UserPlus className="size-4" strokeWidth={1.5} />
        Invite
      </Button>
    );
  }

  const validEmails = emails.filter((email) => EMAIL_RE.test(email));
  const invalidEmails = emails.filter((email) => !EMAIL_RE.test(email));
  // The submit button stays enabled when at least one valid email is
  // chipped — invalid pills get a red ring but don't block sending the
  // good ones.
  const canSubmit = validEmails.length > 0 && !inviteBulk.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setOpen(true);
        } else {
          resetAndClose();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <UserPlus className="size-4" strokeWidth={1.5} />
          Invite
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            // Pull anything still typed in the input into a chip first.
            if (draft.trim()) commitDraft(draft);
            const toSend = (
              draft.trim()
                ? [
                    ...emails,
                    ...draft
                      .trim()
                      .toLowerCase()
                      .split(/[,;\s]+/),
                  ]
                : emails
            ).filter((email) => EMAIL_RE.test(email));
            if (toSend.length === 0) {
              toast.error("Add at least one email address");
              return;
            }
            inviteBulk.mutate({
              organizationId,
              emails: toSend,
              role,
              seatPlanCode: "WONDA",
            });
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Invite members</DialogTitle>
            <DialogDescription>
              Type or paste one or more emails. They&apos;ll receive an
              invitation to accept and join.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-email">Emails</Label>
            <div
              className="flex min-h-10 cursor-text flex-wrap items-center gap-1.5 rounded-md border border-(--hair) bg-(--ground) px-2 py-1.5 focus-within:border-(--hair-strong)"
              onClick={() => {
                const input = document.getElementById("invite-email");
                input?.focus();
              }}
            >
              {emails.map((email) => {
                const isInvalid = !EMAIL_RE.test(email);
                return (
                  <span
                    key={email}
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                      isInvalid
                        ? "bg-(--cli-syntax-red)/15 text-(--cli-syntax-red)"
                        : "border border-(--hair) bg-(--hair) text-(--cli-text)"
                    }`}
                  >
                    {email}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeEmail(email);
                      }}
                      className="rounded-full p-0.5 hover:bg-(--hair)"
                      aria-label={`Remove ${email}`}
                    >
                      <X className="size-3" strokeWidth={1.5} />
                    </button>
                  </span>
                );
              })}
              <input
                id="invite-email"
                type="email"
                value={draft}
                onChange={(event) => {
                  const value = event.target.value;
                  // Comma, semicolon, or whitespace commits the draft as
                  // a chip — same delimiters Gmail accepts.
                  if (/[,;\s]/.test(value)) {
                    commitDraft(value);
                  } else {
                    setDraft(value);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === "Tab") {
                    if (draft.trim()) {
                      event.preventDefault();
                      commitDraft(draft);
                    }
                  } else if (
                    event.key === "Backspace" &&
                    draft === "" &&
                    emails.length > 0
                  ) {
                    // Quality-of-life: backspace on an empty input
                    // pops the last chip, like Gmail.
                    event.preventDefault();
                    setEmails((prev) => prev.slice(0, -1));
                  }
                }}
                onBlur={() => {
                  if (draft.trim()) commitDraft(draft);
                }}
                onPaste={(event) => {
                  const text = event.clipboardData.getData("text");
                  if (/[,;\s]/.test(text)) {
                    event.preventDefault();
                    commitDraft(text);
                  }
                }}
                placeholder={
                  emails.length === 0
                    ? "alice@example.com, bob@example.com"
                    : ""
                }
                className="min-w-[12rem] flex-1 bg-transparent text-sm outline-none placeholder:text-(--cli-text-muted)"
                autoFocus
              />
            </div>
            <p className="text-xs text-(--cli-text-muted)">
              {invalidEmails.length > 0
                ? `${invalidEmails.length} invalid · `
                : ""}
              {validEmails.length}{" "}
              {validEmails.length === 1 ? "address" : "addresses"} ready to
              invite
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="invite-role">Role</Label>
            <Select
              value={role}
              onValueChange={(next: "org:member" | "org:admin") =>
                setRole(next)
              }
            >
              <SelectTrigger id="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org:member">User</SelectItem>
                <SelectItem value="org:admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-(--cli-text-muted)">
            Each member uses a paid Pro seat, billed to the organization and
            assigned automatically when they accept.
          </p>
          <DialogFooter>
            <Button type="submit" variant="steward" disabled={!canSubmit}>
              {inviteBulk.isPending
                ? "Sending..."
                : validEmails.length <= 1
                  ? "Send invitation"
                  : `Send ${validEmails.length} invitations`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type MemberRow = {
  organizationId: string;
  accountId: string;
  seatPlanCode: string | null;
  monthlyCapMicrodollars: bigint | string | number | null;
  isOwner: boolean;
  createdAt: Date;
  updatedAt: Date;
  email: string;
  firstName: string | null;
  lastName: string | null;
  clerkRole: string;
};

type PendingInvitationRow = {
  organizationInvitationId: string;
  email: string;
  role: string;
  createdAt: Date;
};

function PendingInvitationsList({
  organizationId,
  invitations,
  onChanged,
}: {
  organizationId: string;
  invitations: PendingInvitationRow[];
  onChanged: () => void;
}) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const revoke = api.organization.member.revokeInvitation.useMutation({
    onSuccess: () => {
      toast.success("Invitation revoked");
      onChanged();
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <div className="mt-4 border-t border-(--hair) pt-4">
      <p className="text-xs text-(--cli-text-muted)">
        Pending invitations · {invitations.length}
      </p>
      <ul className="mt-2 flex flex-col gap-1">
        {invitations.map((invitation) => (
          <li
            key={invitation.organizationInvitationId}
            className="flex flex-wrap items-center gap-3 rounded-md px-3 py-2 hover:bg-(--hair)"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-(--cli-text-muted) italic">
                {invitation.email}
              </p>
              <p className="mt-0.5 truncate text-xs text-(--cli-text-muted)">
                Invited as {invitation.role === "admin" ? "Admin" : "User"}
              </p>
            </div>
            <StatusPill tone="warning">Invited</StatusPill>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (
                  !window.confirm(
                    `Revoke the invitation sent to ${invitation.email}? Their accept link will stop working.`,
                  )
                ) {
                  return;
                }
                revoke.mutate({
                  organizationId,
                  organizationInvitationId: invitation.organizationInvitationId,
                });
              }}
              disabled={
                revoke.isPending &&
                revoke.variables?.organizationInvitationId ===
                  invitation.organizationInvitationId
              }
            >
              <X className="size-4" strokeWidth={1.5} />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OrgActionsMenu({
  organizationId,
  slug,
  name,
  members,
}: {
  organizationId: string;
  slug: string;
  name: string;
  members: { accountId: string; isOwner: boolean }[];
}) {
  const navigate = useNavigate();
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });
  const { user } = useUser();
  const currentAccountId = user?.publicMetadata.accountId as string | undefined;
  const viewerIsOwner = currentAccountId
    ? (members.find((row) => row.accountId === currentAccountId)?.isOwner ??
      false)
    : false;
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [typedSlug, setTypedSlug] = React.useState("");

  const deleteOrg = api.organization.delete.useMutation({
    onSuccess: () => {
      toast.success("Organization deleted");
      void navigate({ to: "/organizations" });
    },
    onError: (error) => toast.error(error.message),
  });

  if (!viewerIsOwner) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label="Organization actions">
            <MoreHorizontal className="size-4" strokeWidth={1.5} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-(--cli-syntax-red) focus:text-(--cli-syntax-red)"
            onSelect={() => {
              setTypedSlug("");
              setConfirmOpen(true);
            }}
          >
            <Trash2 className="size-4" strokeWidth={1.5} />
            Delete organization
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && deleteOrg.isPending) return;
          setConfirmOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {name}?</DialogTitle>
            <DialogDescription>
              This cancels the organization's subscription, removes every
              member, and makes its name available for new organizations. This
              cannot be undone. Type{" "}
              <code className="rounded bg-(--hair) px-1.5 py-0.5">{slug}</code>{" "}
              to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={typedSlug}
            onChange={(event) => setTypedSlug(event.target.value)}
            placeholder={slug}
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={deleteOrg.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleteOrg.mutate({
                  organizationId,
                  confirmationSlug: typedSlug.trim().toLowerCase(),
                })
              }
              disabled={
                deleteOrg.isPending ||
                typedSlug.trim().toLowerCase() !== slug.toLowerCase()
              }
            >
              {deleteOrg.isPending ? "Deleting..." : "Delete organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MembersTable({
  organizationId,
  members,
  gated,
  onGatedClick,
  onChanged,
}: {
  organizationId: string;
  members: MemberRow[];
  gated: boolean;
  onGatedClick: () => void;
  onChanged: () => void;
}) {
  const api = rootRoute.useRouteContext({ select: (ctx) => ctx.api });

  const kick = api.organization.member.kick.useMutation({
    onSuccess: () => {
      toast.success("Member removed");
      onChanged();
    },
    onError: (error) => toast.error(error.message),
  });
  const setCap = api.organization.member.setCap.useMutation({
    onSuccess: () => {
      toast.success("Limit updated");
      onChanged();
    },
    onError: (error) => toast.error(error.message),
  });
  const setPlan = api.organization.member.setPlan.useMutation({
    onSuccess: () => {
      toast.success("Plan updated");
      onChanged();
    },
    onError: (error) => {
      if (error.data?.code === "PRECONDITION_FAILED") {
        toast.error("Add a payment method to the org before changing plans.");
        onGatedClick();
        return;
      }
      toast.error(error.message);
    },
  });
  const setRole = api.organization.member.setRole.useMutation({
    onSuccess: () => {
      toast.success("Role updated");
      onChanged();
    },
    onError: (error) => toast.error(error.message),
  });
  const transferOwnership =
    api.organization.member.transferOwnership.useMutation({
      onSuccess: () => {
        toast.success("Ownership transferred");
        onChanged();
      },
      onError: (error) => toast.error(error.message),
    });

  const [pendingPlanChange, setPendingPlanChange] = React.useState<{
    accountId: string;
    label: string;
    currentPlan: string | null;
    nextPlan: string | null;
  } | null>(null);

  const [pendingOwnershipTransfer, setPendingOwnershipTransfer] =
    React.useState<{ accountId: string; label: string } | null>(null);

  // Surface the "Make owner" button only on the current owner's view.
  // Other admins can manage members but can't grab ownership — that
  // would let any admin lock the original creator out.
  const { user } = useUser();
  const currentAccountId = user?.publicMetadata.accountId as string | undefined;
  const viewerIsOwner = currentAccountId
    ? (members.find((row) => row.accountId === currentAccountId)?.isOwner ??
      false)
    : false;

  if (members.length === 0) {
    return (
      <p className="text-sm text-(--cli-text-muted)">
        No members yet. Invite someone to get started.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1">
      {members.map((member) => (
        <li
          key={member.accountId}
          className="flex flex-wrap items-center gap-3 rounded-md px-3 py-2 hover:bg-(--hair)"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {[member.firstName, member.lastName].filter(Boolean).join(" ") ||
                member.email}
            </p>
            <p className="mt-0.5 truncate text-xs text-(--cli-text-muted)">
              {member.email}
              {" · Limit: "}
              {member.monthlyCapMicrodollars != null
                ? formatMicrodollars(String(member.monthlyCapMicrodollars))
                : "Unlimited"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <RoleSelect
              isOwner={member.isOwner}
              value={member.clerkRole === "admin" ? "admin" : "member"}
              showOwnerOption={viewerIsOwner && !member.isOwner}
              onChange={(next) => {
                if (next === "owner") {
                  setPendingOwnershipTransfer({
                    accountId: member.accountId,
                    label:
                      [member.firstName, member.lastName]
                        .filter(Boolean)
                        .join(" ") || member.email,
                  });
                  return;
                }
                setRole.mutate({
                  organizationId,
                  accountId: member.accountId,
                  role: next === "admin" ? "org:admin" : "org:member",
                });
              }}
              // Per-row pending: only this row's dropdown disables while
              // its own mutation is in flight; sibling rows stay
              // interactive. mutation.variables holds the in-flight args.
              disabled={
                (setRole.isPending &&
                  setRole.variables?.accountId === member.accountId) ||
                (transferOwnership.isPending &&
                  transferOwnership.variables?.targetAccountId ===
                    member.accountId)
              }
            />
            <PlanSelect
              value={normalizePlanValue(member.seatPlanCode)}
              onChange={(value) => {
                if (gated) {
                  onGatedClick();
                  return;
                }
                const next =
                  value === NO_SEAT_VALUE
                    ? null
                    : (value as (typeof ASSIGNABLE_SEAT_PLANS)[number]);
                if (next === member.seatPlanCode) return;
                setPendingPlanChange({
                  accountId: member.accountId,
                  label:
                    [member.firstName, member.lastName]
                      .filter(Boolean)
                      .join(" ") || member.email,
                  currentPlan: member.seatPlanCode,
                  nextPlan: next,
                });
              }}
              disabled={
                setPlan.isPending &&
                setPlan.variables?.accountId === member.accountId
              }
            />
            {setPlan.isPending &&
            setPlan.variables?.accountId === member.accountId ? (
              <Spinner variant="wonda" size="sm" />
            ) : null}
            <CapButton
              currentMicrodollars={member.monthlyCapMicrodollars}
              gated={gated}
              onGatedClick={onGatedClick}
              onApply={(value) =>
                setCap.mutate({
                  organizationId,
                  accountId: member.accountId,
                  monthlyCapMicrodollars: value,
                })
              }
              disabled={
                setCap.isPending &&
                setCap.variables?.accountId === member.accountId
              }
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (
                  !window.confirm(
                    "Remove this member from the organization? This is immediate.",
                  )
                ) {
                  return;
                }
                kick.mutate({
                  organizationId,
                  accountId: member.accountId,
                });
              }}
              disabled={
                kick.isPending && kick.variables?.accountId === member.accountId
              }
            >
              <X className="size-4" strokeWidth={1.5} />
            </Button>
          </div>
        </li>
      ))}
      <PlanChangeConfirmDialog
        change={pendingPlanChange}
        isPending={setPlan.isPending}
        onCancel={() => setPendingPlanChange(null)}
        onConfirm={() => {
          if (!pendingPlanChange) return;
          const nextPlan =
            pendingPlanChange.nextPlan === null
              ? null
              : (pendingPlanChange.nextPlan as (typeof ASSIGNABLE_SEAT_PLANS)[number]);
          setPlan.mutate(
            {
              organizationId,
              accountId: pendingPlanChange.accountId,
              planCode: nextPlan,
            },
            {
              onSettled: () => setPendingPlanChange(null),
            },
          );
        }}
      />
      <OwnershipTransferConfirmDialog
        transfer={pendingOwnershipTransfer}
        isPending={transferOwnership.isPending}
        onCancel={() => setPendingOwnershipTransfer(null)}
        onConfirm={() => {
          if (!pendingOwnershipTransfer) return;
          transferOwnership.mutate(
            {
              organizationId,
              targetAccountId: pendingOwnershipTransfer.accountId,
            },
            { onSettled: () => setPendingOwnershipTransfer(null) },
          );
        }}
      />
    </ul>
  );
}

function PlanChangeConfirmDialog({
  change,
  isPending,
  onConfirm,
  onCancel,
}: {
  change: {
    accountId: string;
    label: string;
    currentPlan: string | null;
    nextPlan: string | null;
  } | null;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const summary = (() => {
    if (!change) return { title: "", body: "" };
    const { currentPlan, nextPlan, label } = change;
    if (currentPlan === null && nextPlan !== null) {
      return {
        title: `Change plans for ${label}?`,
        body: "The organization's payment method will be charged immediately for the prorated portion of the current cycle. Future renewals will include this seat at the full monthly rate.",
      };
    }
    if (currentPlan !== null && nextPlan === null) {
      return {
        title: `Change plans for ${label}?`,
        body: "Their access ends at the end of the current period. No refund is issued for the unused portion.",
      };
    }
    return {
      title: `Change plans for ${label}?`,
      body: "The organization's payment method will be charged immediately for the prorated difference between the two plans for the rest of the current cycle.",
    };
  })();

  return (
    <Dialog
      open={change !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{summary.title}</DialogTitle>
          <DialogDescription>{summary.body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="steward" onClick={onConfirm} disabled={isPending}>
            {isPending ? "Applying..." : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OwnershipTransferConfirmDialog({
  transfer,
  isPending,
  onConfirm,
  onCancel,
}: {
  transfer: { accountId: string; label: string } | null;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={transfer !== null}
      onOpenChange={(open) => {
        if (!open && !isPending) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {transfer ? `Transfer ownership to ${transfer.label}?` : ""}
          </DialogTitle>
          <DialogDescription>
            They will become the new owner of this organization. You will stay
            on as a regular admin. This is immediate and cannot be undone
            without their cooperation.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="steward" onClick={onConfirm} disabled={isPending}>
            {isPending ? "Transferring..." : "Transfer ownership"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function normalizePlanValue(raw: string | null): string {
  return raw ?? NO_SEAT_VALUE;
}

function RoleSelect({
  isOwner,
  value,
  showOwnerOption,
  onChange,
  disabled,
}: {
  isOwner: boolean;
  value: "admin" | "member";
  // Only the current owner can promote someone else; everyone else sees just User/Admin.
  showOwnerOption: boolean;
  onChange: (value: "admin" | "member" | "owner") => void;
  disabled: boolean;
}) {
  if (isOwner) {
    return <StatusPill tone="warning">Owner</StatusPill>;
  }
  return (
    <Select
      value={value}
      onValueChange={(next: "admin" | "member" | "owner") => onChange(next)}
      disabled={disabled}
    >
      <SelectTrigger className="h-8 w-28 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="member">User</SelectItem>
        <SelectItem value="admin">Admin</SelectItem>
        {showOwnerOption ? <SelectItem value="owner">Owner</SelectItem> : null}
      </SelectContent>
    </Select>
  );
}

function PlanSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  // If the seat is on a plan that's no longer assignable (WONDA_PREMIUM,
  // PREMIUM, CREATOR, etc.), keep it visible in the dropdown so the admin
  // can SEE what they have and still swap to the current plan (WONDA)
  // without losing the read-out.
  const showLegacy =
    value !== NO_SEAT_VALUE &&
    !(ASSIGNABLE_SEAT_PLANS as readonly string[]).includes(value);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-8 w-32 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_SEAT_VALUE}>Basic</SelectItem>
        {ASSIGNABLE_SEAT_PLANS.map((code) => (
          <SelectItem key={code} value={code}>
            {getSeatPlanLabel(code)}
          </SelectItem>
        ))}
        {showLegacy ? (
          <SelectItem value={value}>
            {getSeatPlanLabel(value)} (legacy)
          </SelectItem>
        ) : null}
      </SelectContent>
    </Select>
  );
}

function CapButton({
  currentMicrodollars,
  gated,
  onGatedClick,
  onApply,
  disabled,
}: {
  currentMicrodollars: bigint | string | number | null;
  gated: boolean;
  onGatedClick: () => void;
  onApply: (value: number | null) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  // Use Number division to preserve fractional dollars (e.g., $1.50 → "1.5",
  // not "1"). Treat null distinctly from 0; a $0 cap is valid and means
  // "member cannot spend".
  const initial =
    currentMicrodollars != null
      ? (Number(currentMicrodollars) / 1_000_000).toString()
      : "";
  const [dollars, setDollars] = React.useState(initial);

  if (gated) {
    return (
      <Button variant="ghost" size="sm" onClick={onGatedClick}>
        Set limit
      </Button>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDollars(initial);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          Set limit
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = dollars.trim();
            const parsed = trimmed ? Number(trimmed) : null;
            if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
              toast.error("Enter a non-negative dollar amount or leave blank");
              return;
            }
            const microdollars =
              parsed === null ? null : Math.round(parsed * 1_000_000);
            onApply(microdollars);
            setOpen(false);
          }}
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>Set monthly limit</DialogTitle>
            <DialogDescription>
              Cap how much this member can spend from the org wallet per
              calendar month. Leave blank for no limit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="cap-amount">Amount (USD)</Label>
            <Input
              id="cap-amount"
              value={dollars}
              onChange={(event) => setDollars(event.target.value)}
              placeholder="50"
              inputMode="decimal"
            />
          </div>
          <DialogFooter>
            <Button type="submit" variant="steward" disabled={disabled}>
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatMicrodollars(amount: string | bigint | number): string {
  try {
    const big = BigInt(String(amount));
    const dollars = Number(big) / 1_000_000;
    // Show up to 4 decimals so sub-cent inference charges (e.g. $0.0013 image gen) and the resulting balance ($49.9987) stay legible. minFractionDigits keeps round amounts as $50.00.
    return dollars.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  } catch {
    return "$0.00";
  }
}
